import React, { useState, useEffect } from "react";
import Constants from "../components/Constant.js";
import {
  TextField,
  Button,
  Container,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import MainLayout from "../components/MainLayout.js";
import backgroundImage from "../assets/backgroundHome.png";

export default function VincularUsuarioPrefeitura() {
  const [formData, setFormData] = useState({
    usuario: "",
    prefeitura: "",
  });

  const [errors, setErrors] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [prefeituras, setPrefeituras] = useState([]);
  const [filteredPrefeituras, setFilteredPrefeituras] = useState([]);
  const [searchUsuario, setSearchUsuario] = useState("");
  const [searchPrefeitura, setSearchPrefeitura] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  // Carrega os usuários da API
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const response = await fetch(Constants.API_CONSULTAR_USUARIO, {
          headers: {
            "Authorization": `Bearer ${token}` // Adiciona o token no cabeçalho
          }
        });
        const data = await response.json();
       // console.log("Dados da API (usuários):", data); // Depuração
        if (response.ok && data.users) {
          setUsuarios(data.users);
          setFilteredUsuarios(data.users);
        } else {
          setSnackbarMessage("Erro ao buscar usuários.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Erro ao conectar com o servidor:", error);
        setSnackbarMessage("Erro ao conectar com o servidor.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchUsuarios();
  }, [token]);

  // Filtra os usuários com base na pesquisa
  useEffect(() => {
    if (searchUsuario) {
      const filtered = usuarios.filter((usuario) =>
        usuario.USU_NOME.toLowerCase().includes(searchUsuario.toLowerCase())
      );
      setFilteredUsuarios(filtered);
    } else {
      setFilteredUsuarios(usuarios);
    }
  }, [searchUsuario, usuarios]);

  // Carrega as prefeituras da API
  useEffect(() => {
    const fetchPrefeituras = async () => {
      try {
        const response = await fetch(Constants.API_CONSULTAR_PREFEITURA, {
          headers: {
            "Authorization": `Bearer ${token}` // Adiciona o token no cabeçalho
          }
        });
        const data = await response.json();
       // console.log("Dados da API (prefeituras):", data); // Depuração
        if (response.ok && data.prefeituras) {
          setPrefeituras(data.prefeituras);
          setFilteredPrefeituras(data.prefeituras);
        } else {
          setSnackbarMessage("Erro ao buscar prefeituras.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Erro ao conectar com o servidor:", error);
        setSnackbarMessage("Erro ao conectar com o servidor.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchPrefeituras();
  }, [token]);

  // Filtra as prefeituras com base na pesquisa
  useEffect(() => {
    if (searchPrefeitura) {
      const filtered = prefeituras.filter((prefeitura) =>
        prefeitura.PRE_NOME.toLowerCase().includes(searchPrefeitura.toLowerCase())
      );
      setFilteredPrefeituras(filtered);
    } else {
      setFilteredPrefeituras(prefeituras);
    }
  }, [searchPrefeitura, prefeituras]);

  // Valida o formulário
  const validateForm = () => {
    const newErrors = {};
    if (!formData.usuario) newErrors.usuario = "Usuário é obrigatório.";
    if (!formData.prefeitura) newErrors.prefeitura = "Prefeitura é obrigatória.";

    // Verifica se o usuário selecionado existe na lista
    if (formData.usuario && !usuarios.some((user) => user.USU_ID === formData.usuario)) {
      newErrors.usuario = "Usuário inválido.";
    }

    // Verifica se a prefeitura selecionada existe na lista
    if (formData.prefeitura && !prefeituras.some((pref) => pref.PRE_ID === formData.prefeitura)) {
      newErrors.prefeitura = "Prefeitura inválida.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Envia os dados do formulário para a API
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const requestData = {
        PRU_USU_ID: formData.usuario,
        PRU_PRE_ID: formData.prefeitura,
      };

      //console.log("Dados enviados para a API:", JSON.stringify(requestData, null, 2));

      try {
        const response = await fetch(Constants.API_VINCULAR_USUARIO_PREFEITURA, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
            "Authorization": `Bearer ${token}` // Adiciona o token no cabeçalho
          },
          body: JSON.stringify(requestData),
        });

        const responseData = await response.json();
       // console.log("Resposta da API (vincularPrefeitura):", responseData); // Depuração

        if (response.ok) {
          setSnackbarMessage(responseData.message || "Usuário vinculado com sucesso!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          handleCancel(); // Limpa o formulário após o sucesso
        } else {
          const errorMessages = responseData.erros
            ? Object.values(responseData.erros).flat().join(", ")
            : responseData.message || "Erro desconhecido";
          setSnackbarMessage(`Erro ao vincular usuário: ${errorMessages}`);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Erro ao enviar dados:", error);
        setSnackbarMessage("Erro ao conectar com o servidor.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  // Limpa o formulário
  const handleCancel = () => {
    setFormData({ usuario: "", prefeitura: "" });
    setErrors({});
    setSearchUsuario("");
    setSearchPrefeitura("");
  };

  // Fecha o Snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <MainLayout>
      <Box
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backgroundBlendMode: "overlay",
          padding: 3,
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ p: 4, bgcolor: "background.paper", borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h4" align="center" gutterBottom>
              Vincular Usuário à Prefeitura
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={filteredUsuarios}
                    getOptionLabel={(option) => option.USU_NOME || ""}
                    value={usuarios.find((user) => user.USU_ID === formData.usuario) || null}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({ ...prev, usuario: newValue ? newValue.USU_ID : "" }));
                    }}
                    onInputChange={(_, newInputValue) => {
                      setSearchUsuario(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Usuário"
                        fullWidth
                        margin="normal"
                        error={!!errors.usuario}
                        helperText={errors.usuario}
                        sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={filteredPrefeituras}
                    getOptionLabel={(option) => option.PRE_NOME || ""}
                    value={prefeituras.find((pref) => pref.PRE_ID === formData.prefeitura) || null}
                    onChange={(_, newValue) => {
                      setFormData((prev) => ({ ...prev, prefeitura: newValue ? newValue.PRE_ID : "" }));
                    }}
                    onInputChange={(_, newInputValue) => {
                      setSearchPrefeitura(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Prefeitura"
                        fullWidth
                        margin="normal"
                        error={!!errors.prefeitura}
                        helperText={errors.prefeitura}
                        sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Button type="button" variant="outlined" color="secondary" onClick={handleCancel} fullWidth sx={{ height: "56px" }}>
                  Cancelar
                </Button>
                <Button type="submit" variant="contained" color="primary" fullWidth sx={{ height: "56px" }}>
                  Vincular
                </Button>
              </Box>
            </form>
          </Box>
        </Container>
      </Box>

      {/* Snackbar para mensagens de erro e sucesso */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
}